import * as React from "react";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva } from "class-variance-authority";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "@/lib/utils";

function NavigationMenu({
    className,
    children,
    viewport = true,
    ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root> & {
    viewport?: boolean;
}) {
    return (
        <NavigationMenuPrimitive.Root
            data-slot="navigation-menu"
            data-viewport={viewport}
            className={cn(
                "setu:group/navigation-menu setu:relative setu:flex setu:max-w-max setu:flex-1 setu:items-center setu:justify-center",
                className
            )}
            {...props}
        >
            {children}
            {viewport && <NavigationMenuViewport />}
        </NavigationMenuPrimitive.Root>
    );
}

function NavigationMenuList({ className, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.List>) {
    return (
        <NavigationMenuPrimitive.List
            data-slot="navigation-menu-list"
            className={cn(
                "setu:group setu:flex setu:flex-1 setu:list-none setu:items-center setu:justify-center setu:gap-1",
                className
            )}
            {...props}
        />
    );
}

function NavigationMenuItem({ className, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.Item>) {
    return (
        <NavigationMenuPrimitive.Item
            data-slot="navigation-menu-item"
            className={cn("setu:relative", className)}
            {...props}
        />
    );
}

const navigationMenuTriggerStyle = cva(
    "setu:group setu:inline-flex setu:h-9 setu:w-max setu:items-center setu:justify-center setu:rounded-md setu:bg-background setu:px-4 setu:py-2 setu:text-sm setu:font-medium setu:hover:bg-accent setu:hover:text-accent-foreground setu:focus:bg-accent setu:focus:text-accent-foreground setu:disabled:pointer-events-none setu:disabled:opacity-50 setu:data-[state=open]:hover:bg-accent setu:data-[state=open]:text-accent-foreground setu:data-[state=open]:focus:bg-accent setu:data-[state=open]:bg-accent/50 setu:focus-visible:ring-ring/50 setu:outline-none setu:transition-[color,box-shadow] setu:focus-visible:ring-[3px] setu:focus-visible:outline-1"
);

function NavigationMenuTrigger({
    className,
    children,
    ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger>) {
    return (
        <NavigationMenuPrimitive.Trigger
            data-slot="navigation-menu-trigger"
            className={cn(navigationMenuTriggerStyle(), "setu:group", className)}
            {...props}
        >
            {children}{" "}
            <ChevronDownIcon
                className="setu:relative setu:top-[1px] setu:ml-1 setu:size-3 setu:transition setu:duration-300 setu:group-data-[state=open]:rotate-180"
                aria-hidden="true"
            />
        </NavigationMenuPrimitive.Trigger>
    );
}

function NavigationMenuContent({ className, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.Content>) {
    return (
        <NavigationMenuPrimitive.Content
            data-slot="navigation-menu-content"
            className={cn(
                "setu:data-[motion^=from-]:animate-in setu:data-[motion^=to-]:animate-out setu:data-[motion^=from-]:fade-in setu:data-[motion^=to-]:fade-out setu:data-[motion=from-end]:slide-in-from-right-52 setu:data-[motion=from-start]:slide-in-from-left-52 setu:data-[motion=to-end]:slide-out-to-right-52 setu:data-[motion=to-start]:slide-out-to-left-52 setu:top-0 setu:left-0 setu:w-full setu:p-2 setu:pr-2.5 setu:md:absolute setu:md:w-auto",
                "setu:group-data-[viewport=false]/navigation-menu:bg-popover setu:group-data-[viewport=false]/navigation-menu:text-popover-foreground setu:group-data-[viewport=false]/navigation-menu:data-[state=open]:animate-in setu:group-data-[viewport=false]/navigation-menu:data-[state=closed]:animate-out setu:group-data-[viewport=false]/navigation-menu:data-[state=closed]:zoom-out-95 setu:group-data-[viewport=false]/navigation-menu:data-[state=open]:zoom-in-95 setu:group-data-[viewport=false]/navigation-menu:data-[state=open]:fade-in-0 setu:group-data-[viewport=false]/navigation-menu:data-[state=closed]:fade-out-0 setu:group-data-[viewport=false]/navigation-menu:top-full setu:group-data-[viewport=false]/navigation-menu:mt-1.5 setu:group-data-[viewport=false]/navigation-menu:overflow-hidden setu:group-data-[viewport=false]/navigation-menu:rounded-md setu:group-data-[viewport=false]/navigation-menu:!border setu:group-data-[viewport=false]/navigation-menu:shadow setu:group-data-[viewport=false]/navigation-menu:duration-200 **:data-[slot=navigation-menu-link]:focus:ring-0 **:data-[slot=navigation-menu-link]:focus:outline-none",
                className
            )}
            {...props}
        />
    );
}

function NavigationMenuViewport({
    className,
    ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Viewport>) {
    return (
        <div
            className={cn(
                "setu:absolute setu:top-full setu:left-0 setu:isolate setu:z-50 setu:flex setu:justify-center"
            )}
        >
            <NavigationMenuPrimitive.Viewport
                data-slot="navigation-menu-viewport"
                data-testid="navigation-menu-viewport"
                className={cn(
                    "setu:origin-top-center setu:bg-popover setu:text-popover-foreground setu:data-[state=open]:animate-in setu:data-[state=closed]:animate-out setu:data-[state=closed]:zoom-out-95 setu:data-[state=open]:zoom-in-90 setu:relative setu:mt-1.5 setu:h-[var(--radix-navigation-menu-viewport-height)] setu:w-full setu:overflow-hidden setu:rounded-md setu:!border setu:shadow setu:md:w-[var(--radix-navigation-menu-viewport-width)]",
                    className
                )}
                {...props}
            />
        </div>
    );
}

function NavigationMenuLink({ className, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.Link>) {
    return (
        <NavigationMenuPrimitive.Link
            data-slot="navigation-menu-link"
            className={cn(
                "setu:data-[active=true]:focus:bg-accent setu:data-[active=true]:hover:bg-accent setu:data-[active=true]:bg-accent/50 setu:data-[active=true]:text-accent-foreground setu:hover:bg-accent setu:hover:text-accent-foreground setu:focus:bg-accent setu:focus:text-accent-foreground setu:focus-visible:ring-ring/50 setu:[&_svg:not([class*='text-'])]:text-muted-foreground setu:flex setu:flex-col setu:gap-1 setu:rounded-sm setu:p-2 setu:text-sm setu:transition-all setu:outline-none setu:focus-visible:ring-[3px] setu:focus-visible:outline-1 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        />
    );
}

function NavigationMenuIndicator({
    className,
    ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Indicator>) {
    return (
        <NavigationMenuPrimitive.Indicator
            data-slot="navigation-menu-indicator"
            data-testid="navigation-menu-indicator"
            className={cn(
                "setu:data-[state=visible]:animate-in setu:data-[state=hidden]:animate-out setu:data-[state=hidden]:fade-out setu:data-[state=visible]:fade-in setu:top-full setu:z-[1] setu:flex setu:h-1.5 setu:items-end setu:justify-center setu:overflow-hidden",
                className
            )}
            {...props}
        >
            <div className="setu:!bg-border setu:relative setu:top-[60%] setu:h-2 setu:w-2 setu:rotate-45 setu:rounded-tl-sm setu:shadow-md" />
        </NavigationMenuPrimitive.Indicator>
    );
}

export {
    NavigationMenu,
    NavigationMenuList,
    NavigationMenuItem,
    NavigationMenuContent,
    NavigationMenuTrigger,
    NavigationMenuLink,
    NavigationMenuIndicator,
    NavigationMenuViewport,
    navigationMenuTriggerStyle,
};
